/**
 * @module Components.Routes
 *
 */
import React, { useState, useEffect, useCallback, useContext } from "react";

import styles from "./styles.module.scss";
import ProgramIcon from "../../../../public/images/group-program.svg";
import { useHistory } from "react-router-dom";
import {
  schedulingFetchDelete,
  schedulingFetchGet,
} from "../../../utils/request/scheduling/fetch";
import ItemListView from "../../../components/ItemListView";
import ProgramItem from "../../../components/Programs/ProgramItem";
import ProgramFilters from "app/components/Programs/ProgramFilters";
import FilterImage from "../../../../public/images/filter.svg";
import SearchImage from "../../../../public/images/search.svg";
import { debounce, truncate } from "lodash";
import { useTranslation } from "react-i18next";
import TextAreaModal from "app/components/Programs/TextAreaModal";
import CalendarX from "../../../../public/images/calendar-x-red.svg";
import { ProgramState } from "app/reducers/scheduling/program";
import { useDispatch } from "react-redux";
import { resetProgramSettings, setDefaultImages } from "app/actions/scheduling/program";
import { Loading } from "app/components/Wrappers";
import { UserContext } from "app/contexts/UserContext";
import Navbar from "app/components/Navbar";
import ButtonVariant from "app/components/Button/ButtonVariant";
import Tabs from "app/components/Tabs";
import ProgramDeletedSuccessModal from "app/components/Programs/ProgramDeletedSuccessModal";

interface Programs {
  active: ProgramState[];
  completed: ProgramState[];
}

export default function ManageCoachPrograms() {
  const { t } = useTranslation();
  const [refetch, setRefetch] = useState(true);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [programCategories, setProgramCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [programs, setPrograms] = useState<Programs>();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [cancelWithoutRefund, setCancelWithoutRefund] = useState(false);
  const [programId, setProgramId] = useState(null);
  const [activeCount, setActiveCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  const screenWidth = window.innerWidth;
  const dispatch = useDispatch();
  const user = useContext(UserContext);

  const initialFilters = {
    name: "",
    category: null,
    attendance: null,
    coach: null,
    registration_status: null,
    tags: [],
  };
  const [filters, setFilters] = useState(initialFilters);
  useEffect(() => {
    schedulingFetchGet("/program_categories").then((response) => {
      const categories = response?.map((item, index) => {
        return { label: item, value: index };
      });
      setProgramCategories(categories);
    });
  }, []);

  useEffect(() => {
    setRefetch(true);
    setPrograms(undefined);
  }, [selectedTab]);

  useEffect(() => {
    if (refetch) {
      setLoading(true);
      const selectedFilters = {
        name,
        category: filters.category?.label,
        attendance: filters.attendance?.label.toLowerCase(),
        coach_profile_id: filters.coach ? [filters.coach?.id] : null,
        registration_status: filters.registration_status?.value,
        tag_ids:
          filters.tags?.length > 0
            ? filters.tags.map((item) => item?.value)
            : null,
        status: selectedTab === 0 ? "active" : "completed",
      };
      schedulingFetchGet("/programs", selectedFilters, { version: 3 }).then((response) => {
        setPrograms(response.data);
        setActiveCount(response.data.activeCount);
        setCompletedCount(response.data.completedCount);
        setRefetch(false);
        setLoading(false);
      });
    }
  }, [refetch, filters, name]);
  const history = useHistory();

  const onAddPress = () => {
    dispatch(resetProgramSettings());
    history.push("/programs/build");
  };

  const handleInputChange = (event) => {
    debouncedFunction(event.target.value);
  };

  const renderEmpty = (title: string, action: string) => {
    return (
      <div className={styles.empty}>
        <div>
          <img
            style={{
              width: "30px",
              height: "30px",
            }}
            src={ProgramIcon}
          />
        </div>
        <div className={styles.text_container}>
          <p className={styles.bold}>{title}</p>
          <p>{t("no_group_programs_yet", { programType: action })}</p>
        </div>
      </div>
    );
  };

  const onApplyFilters = useCallback(() => {
    setRefetch(true);
    setShowFilters(false);
    return null;
  }, []);

  const debouncedResults = useCallback(
    () =>
      debounce((value) => {
        setName(value);
        onApplyFilters();
      }, 1000),
    [onApplyFilters],
  );

  const debouncedFunction = debouncedResults();

  const handleDeletePopup = (id) => {
    setDeleteModal(true);
    setProgramId(id);
  };

  const handleDeleteProgram = () => {
    schedulingFetchDelete(`/programs/${programId}`, {
      apply_to_recurrence: "all",
      reason: deleteMessage,
      ...(cancelWithoutRefund ? { skip_refund: true } : {}),
    }).then(() => {
      // Remove the deleted program from local state
      setPrograms((prevPrograms) => {
        if (!prevPrograms) {
          return prevPrograms;
        }
        return {
          active: prevPrograms.active?.filter((program) => program.id !== programId),
          completed: prevPrograms.completed?.filter((program) => program.id !== programId),
        };
      });

      // Close delete confirmation modal and show success modal
      setDeleteModal(false);
      setDeleteMessage("");
      setCancelWithoutRefund(false);
      setShowDeleteSuccessModal(true);
    });
  };

  const handleCloseSuccessModal = () => {
    setShowDeleteSuccessModal(false);
    setProgramId(null);
    setSelectedItem(null);
  };

  const renderTab = (programList, isActive) => {
    const showActivePrograms = selectedTab === 0;

    const title = showActivePrograms
      ? t("Active Programs")
      : t("Completed Programs");
    const action = showActivePrograms ? t("active") : t("completed");

    if (loading) {
      return <Loading isLoading loadType="spinner" />;
    }

    return programList?.length > 0 ? (
      <div className={styles.padding_16}>
        <ItemListView
          Item={ProgramItem}
          isActive={isActive}
          objectList={programList}
          selectedItem={selectedItem}
          handleDelete={(id) => handleDeletePopup(id)}
          setSelectedItem={setSelectedItem}
        />
      </div>
    ) : (
      renderEmpty(title, action)
    );
  };

  const tabs = [
    {
      label: `${t("Active")} (${activeCount || programs?.active?.length || 0})`,
      onClickHandler: () => setSelectedTab(0),
      isActive: selectedTab === 0
    },
    {
      label: `${t("Completed")} (${completedCount || programs?.completed?.length || 0})`,
      onClickHandler: () => setSelectedTab(1),
      isActive: selectedTab === 1
    }
  ];

  useEffect(() => {
    schedulingFetchGet("/default_images").then((response) => {
      dispatch(setDefaultImages(response));
    });
  }, []);

  return (
    <div className={styles.position}>
      <Navbar label={t("Programs")} hasSearch={false} hasNotifications={false}>
        <ButtonVariant
          title={t("Add Program")}
          icon={<i className="ico ico-add" />}
          buttonType="secondary-filled"
          childStyles={styles.create_btn_inner}
          buttonStyles={styles.create_btn}
          onClickFunc={onAddPress}
        />
      </Navbar>

      <div className={styles.mobile_navbar}>
        <h3 className={styles.h3_title}>Programs</h3>
        <ButtonVariant
          title={t("Add Program")}
          icon={<i className="ico ico-add" />}
          buttonType="secondary-filled"
          childStyles={styles.create_btn_inner}
          buttonStyles={styles.create_btn}
          onClickFunc={onAddPress}
        />
      </div>

      <Tabs tabs={tabs} />

      <div className={`${styles.empty_container} programs-page-container`}>
        <div className={styles.line_container}>
          <div className={styles.search_container}>
            <input
              className={styles.search}
              placeholder={`${t("Search Program")}...`}
              onChange={handleInputChange}
            />
            <img src={SearchImage} />
          </div>
          <div
            className={styles.filter_button}
            onClick={() => setShowFilters((old) => !old)}
          >
            <img src={FilterImage} />
          </div>
        </div>
        {renderTab(
          selectedTab === 0 ? programs?.active : programs?.completed,
          selectedTab === 0 ? true : false,
        )}
      </div>
      {showFilters && (
        <ProgramFilters
          facilityId={user?.facilityId}
          filters={filters}
          setFilters={setFilters}
          onApply={onApplyFilters}
          programCategories={programCategories}
          onReset={() => setFilters(initialFilters)}
        />
      )}
      {deleteModal && (
        <TextAreaModal
          topImage={CalendarX}
          stackedButtons
          showCloseIcon
          title={t("Delete Program?")}
          subTitle={
            <>
              {t("Are you sure you want to delete")} {truncate(
                selectedItem.title,
                { length: 25 },
              )}? {t(
                "All the registered, waitlisted athletes and coaches will be notified via email.",
              )}
              {(selectedItem?.collectType === "online" ||
                selectedItem?.collectType === "both") &&
                !cancelWithoutRefund && (
                <>
                  <br />
                  <br />
                  <strong>
                    {t("All online paying participants will receive a full refund through Stripe.")}
                  </strong>
                </>
              )}
            </>
          }
          additionalContent={
            (selectedItem?.collectType === "online" ||
              selectedItem?.collectType === "both") && (
              <label className={styles.cancel_without_refund}>
                <input
                  type="checkbox"
                  checked={cancelWithoutRefund}
                  onChange={(event) => setCancelWithoutRefund(event.target.checked)}
                />
                {t("Cancel without issuing Stripe refunds")}
              </label>
            )
          }
          placeholder={t(
            "Ex: Today's clinic is canceled due to inclement weather.",
          )}
          width={screenWidth > 400 ? "400px" : "300px"}
          value={deleteMessage}
          setValue={setDeleteMessage}
          cancelText={t("No, keep it")}
          confirmFirst
          confirmText={t("Yes, delete it")}
          setShowModal={(value) => {
            setDeleteModal(value);
            if (!value) { setCancelWithoutRefund(false); }
          }}
          handleAction={handleDeleteProgram}
        />
      )}
      <ProgramDeletedSuccessModal
        isOpen={showDeleteSuccessModal}
        onClose={handleCloseSuccessModal}
      />
    </div>
  );
}
